import type { EntityState } from '@reduxjs/toolkit';

import type { TMessage } from '~entities/messages';
import { getBotsApi } from '~services/api/bots/dynamic';
import { api } from '~shared/api/origin';
import {
	adaptAudioMessage,
	adaptSextingMessage,
	adaptVideoMessage,
	addEncodedTextFieldToMessage,
	addGifPath,
	formatIMGPaths,
	normalizeFormat,
	transformMessageBodyToMessage,
} from '~utils/messages';

import { messagesAdapter, messagesSelector } from './lib';
import type { SendMessageApiRequest } from './types';

export const {
	endpoints,
	util,
	useGetMessagesQuery,
	useReadMessageMutation,
	useSendMessageMutation,
	useRegenerateMessageMutation,
	useDeleteMessageMutation,
	useBuyMessageMutation,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getMessages: build.query<
			{
				receivedCount: number;
				messages: EntityState<TMessage>;
				totalLength: number;
			},
			{
				id: string;
				recipientId: string;
				omit: number;
			}
		>({
			query: ({ id, recipientId, omit }) => ({
				url: `v2/dialogs/messages/sender/${id}/recipient/${recipientId}`,
				params: { omit, select: 30 },
				headers: {
					Accept: 'application/json+videos',
				},
			}),
			transformResponse: (response: TMessage[] | null) => {
				if (response === null) {
					return {
						receivedCount: 0,
						totalLength: 0,
						messages: messagesAdapter.getInitialState(),
					};
				}
				const transformed = response
					?.map(addEncodedTextFieldToMessage)
					.map(adaptAudioMessage)
					.map(normalizeFormat)
					.map(addGifPath)
					.map(formatIMGPaths)
					.map(adaptSextingMessage)
					.map(adaptVideoMessage);

				const messages = messagesAdapter.upsertMany(
					messagesAdapter.getInitialState(),
					transformed
				);
				return {
					receivedCount: response.length,
					messages,
					totalLength: messages.ids.length,
				};
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					// @ts-ignore
					(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
				);
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.recipientId}-${queryArgs?.id}`;
			},
			merge: (currentState, incomingState) => {
				messagesAdapter.setAll(currentState.messages, [
					...messagesSelector.selectAll(incomingState.messages),
					...messagesSelector.selectAll(currentState.messages),
				]);
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;
			},
			// Example invalidate with param
			// providesTags: (result, error, { recipientId }) => [
			// 	{ type: apiTags.conversation, id: recipientId }, // ? as const
			// ],
		}),
		readMessage: build.mutation<
			undefined,
			{ senderId: string; recipientId: string; messageId: number }
		>({
			query: ({ senderId, recipientId, messageId }) => ({
				// TODO: This endpoint mentioned in CS, but does not not work for me, 202 Accepted, but counter is not reseted
				// url: `/dialogs/messages/sender/${senderId}/recipient/${recipientId}/${messageId}`,
				url: `/dialogs/messages/${senderId}:${recipientId}/${messageId}`,
				method: 'PATCH',
				body: JSON.stringify({ read: true }),
				headers: {
					'content-type': 'application/json',
				},
			}),
			async onQueryStarted(
				{ senderId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				const botsApi = await getBotsApi();
				const dialogPatch = dispatch(
					botsApi.util.updateQueryData(
						'getContacts',
						{ userId: recipientId },
						(draft) => {
							const bot = Object.values(draft.contacts).find(
								({ clientId }) =>
									clientId === +recipientId || clientId === +senderId
							);
							if (!bot) {
								return;
							}

							bot.unreadMessagesCount = 0;
						}
					)
				);

				queryFulfilled.catch(() => {
					dialogPatch.undo();
				});
			},
		}),
		sendMessage: build.mutation<
			TMessage[],
			{ id: string; recipientId: string; message: SendMessageApiRequest }
		>({
			query: ({ id, recipientId, message }) => ({
				url: `/dialogs/messages/sender/${id}/recipient/${recipientId}`,
				method: 'POST',
				body: message,
			}),
			async onQueryStarted(
				{ id, recipientId, message },
				{ dispatch, queryFulfilled }
			) {
				const messagesPatch = dispatch(
					util.updateQueryData(
						'getMessages',
						{ id, recipientId, omit: 0 },
						(draft) => {
							const lastTag = messagesSelector.selectIds(draft.messages)[
								messagesSelector.selectTotal(draft.messages) - 1
							];
							const prevId =
								messagesSelector.selectById(draft.messages, lastTag)?.id || 0;

							const msg = transformMessageBodyToMessage(
								message,
								id,
								recipientId,
								prevId
							);
							messagesAdapter.addOne(draft.messages, msg);
							draft.totalLength += 1;
						}
					)
				);
				const botsApi = await getBotsApi();
				const contactsPatch = dispatch(
					botsApi.util.updateQueryData(
						'getContacts',
						{ userId: id },
						(draft) => {
							const bot = Object.values(draft.contacts).find(
								({ clientId }) => clientId === +recipientId
							);
							if (!bot) {
								return;
							}
							bot.totalMessagesCount += 1;
							bot.unreadMessagesCount = 0;
							bot.lastMessage = {
								id: Date.now(),
								senderId: id,
								recipientId,
								text: message.text,
								isSexting: false,
								paid: true,
								timestamp: Date.now(),
							};
						}
					)
				);

				queryFulfilled.catch(() => {
					messagesPatch.undo();
					contactsPatch.undo();
				});
			},
		}),
		regenerateMessage: build.mutation<
			{},
			{ userId: string; recipientId: string; messageId: number }
		>({
			query: ({ userId, recipientId, messageId }) => ({
				url: `/ai/users/${userId}/${recipientId}/regenerate-answer`,
				method: 'POST',
				body: {
					messageId,
				},
			}),
		}),
		deleteMessage: build.mutation<
			{},
			{ senderId: string; recipientId: string; messageId: number }
		>({
			query: ({ senderId, recipientId, messageId }) => ({
				url: `/dialogs/messages/${senderId}:${recipientId}/${messageId}`,
				method: 'DELETE',
			}),
			onQueryStarted(
				{ senderId, recipientId, messageId },
				{ dispatch, queryFulfilled }
			) {
				const deleteDispatch = dispatch(
					util.updateQueryData(
						'getMessages',
						{ id: recipientId, recipientId: senderId, omit: 0 },
						(draft) => {
							const allMes = messagesSelector.selectAll(draft.messages);
							const message = allMes.find(({ id }) => id === messageId);

							if (!message) return;
							messagesAdapter.removeOne(draft.messages, message.tag);
						}
					)
				);

				queryFulfilled.catch(() => {
					deleteDispatch.undo();
				});
			},
		}),
		buyMessage: build.mutation<
			{ Success: boolean; Data: { Text: string } },
			{ id: string; recipientId: string; messageId: number; mediaId?: string }
		>({
			query: ({ id, recipientId, messageId }) => ({
				url: `/dialogs/messages/sender/${recipientId}/recipient/${id}/${messageId}/buy`,
				method: 'POST',
			}),
			async onQueryStarted(
				{ id, recipientId, messageId },
				{ dispatch, queryFulfilled }
			) {
				try {
					const { data: res } = await queryFulfilled;
					if (!res.Success) return;
					const botsApi = await getBotsApi();
					const text = res.Data.Text;
					dispatch(
						util.updateQueryData(
							'getMessages',
							{ id, recipientId, omit: 0 },
							(draft) => {
								const message = messagesSelector
									.selectAll(draft.messages)
									.find(({ id }) => id === messageId);
								if (!message) {
									return;
								}
								messagesAdapter.updateOne(draft.messages, {
									id: message.tag,
									changes: {
										text,
										meta: {
											...message.meta,
											tariffication: {
												...message.meta?.tariffication,
												paid: true,
												final: true,
												cost: 0,
											},
										},
									},
								});
							}
						)
					);
					dispatch(
						botsApi.util.updateQueryData(
							'getContacts',
							{ userId: id },
							(draft) => {
								const bot = Object.values(draft.contacts).find(
									({ clientId }) => clientId === +recipientId
								);
								if (bot?.lastMessage?.id !== messageId) {
									return;
								}
								bot.lastMessage.paid = true;
								bot.lastMessage.cost = 0;
								bot.lastMessage.text = text;
							}
						)
					);
				} catch {}
			},
		}),
	}),
});
